import { DBO_TM } from '@op/utils/format';
import {
  channelRenderHelper,
  languageRenderHelper,
} from '../renderHelper/value';
import { DownloadConfigInfoType, CommonColumnsType } from '../types';
import { getFeedsComments } from '@op/services';
import { getVideoSummary } from '@op/pages/community/video/hooks';
import { METRIC_OPTIONS } from '@op/pages/community/video/components/TagAnalysisCard';

export const VideoTableDownload: DownloadConfigInfoType = {
  type: 'page',
  columns: ({ auxiliaryParams }): CommonColumnsType => {
    const {
      hasExtInfo = false,
      hasVideoAITag = false,
      hasEngagementRate = false,
    } = auxiliaryParams || {};
    const list: CommonColumnsType = [
      { header: 'Channel', key: 'channel_name' },
      {
        header: 'Poster',
        key: 'reviewer',
      },
      { header: 'Poster URL', key: 'author_url' },
      {
        header: 'Handle Name(Youtube)',
        key: 'author_handle_name',
      },
      {
        header: 'Content',
        key: 'content',
      },
      { header: 'URL', key: 'content_url' },
      {
        header: 'Time(UTC+8:00)',
        key: 'comment_time',
      },
      {
        header: 'Durations',
        key: 'video_detail',
        renderHelper: [
          (item: { [x: string]: any }, key: string | number) => {
            const value = item[key];
            try {
              const info = JSON.parse(value);
              return DBO_TM.duration(Number(info.duration), '-');
            } catch (error) {
              return '-';
            }
          },
        ],
      },
      {
        header: 'Language',
        key: 'language',
        renderHelper: [languageRenderHelper],
      },
      {
        header: 'View',
        key: 'view',
        numFmt: '#,##',
      },
      {
        header: 'Like',
        key: 'tweets_like',
        numFmt: '#,##',
      },
      {
        header: 'Dislike',
        key: 'tweets_unlike',
        numFmt: '#,##',
      },
      {
        header: 'Comment',
        key: 'tweets_reply',
        numFmt: '#,##',
      },

      {
        header: 'Share',
        key: 'tweets_retweet',
        numFmt: '#,##',
      },
      {
        header: 'Follower/Subscriber',
        key: 'subscribe',
        numFmt: '#,##',
      },
      {
        header: 'Engagement',
        key: 'engagement',
        numFmt: '#,##',
      },
      ...(hasEngagementRate
        ? [
            {
              header: 'Engagement Rate',
              key: 'engagement_per_view',
              numFmt: '0.00%',
            },
          ]
        : []),
      {
        header: 'Video Content Sentiment',
        key: 'summary_sentiment',
      },
      {
        header: 'Video Content Summary CN',
        key: 'summary_cn',
      },
      {
        header: 'Video Content Summary EN',
        key: 'summary_en',
      },
      {
        header: 'AI Live stream comments Sentiment',
        key: 'd_summary_sentiment',
      },
      {
        header: 'AI Live Stream Comments Summary CN',
        key: 'd_summary_cn',
      },
      {
        header: 'AI Live Stream Comments Summary EN',
        key: 'd_summary_en',
      },
    ];

    // if (hasExtInfo) {
    //   list.push(
    //     {
    //       header: 'Selling Points',
    //       key: 'selling_points',
    //     },
    //     {
    //       header: 'Video Category',
    //       key: 'video_categories',
    //     },
    //   );
    // }

    if (hasVideoAITag) {
      list.push(
        {
          header: 'AI Tag (English)',
          key: 'ai_video_tags_en',
        },
        {
          header: 'AI Tag (Chinese)',
          key: 'ai_video_tags_cn',
        },
      );
    }

    list.push({
      header: 'Description',
      key: 'description',
    });
    return list;
  },
  downloadFunc: async (params: any) => {
    const { list, total } = await getFeedsComments(params);
    const gameInfo = {
      edition_unified_id: params.edition_unified_id,
      entity_type: params.entity_type,
      id_type: params.id_type,
    };
    const gptSubTitleSummary = await getVideoSummary(
      { list },
      'subtitle',
      gameInfo,
      params,
    );
    const gptDanmakuSummary = await getVideoSummary(
      { list },
      'danmaku',
      gameInfo,
      params,
    );

    const data = list.map((item: any) => {
      const key = item.comment_uin;
      const {
        summary_cn,
        summary_en,
        summary_sentiment,
        selling_points,
        video_categories,
      } = gptSubTitleSummary?.[key] || {};
      const {
        summary_cn: d_summary_cn,
        summary_en: d_summary_en,
        summary_sentiment: d_summary_sentiment,
      } = gptDanmakuSummary?.[key] || {};

      const obj: any = {};
      // -1等于无数据 设置成0
      Object.keys(item).forEach(itemKey => {
        if (item[itemKey] === -1) {
          obj[itemKey] = 0;
        }
      });

      let ai_video_tags_en = '-';
      let ai_video_tags_cn = '-';
      if (item?.video_tags) {
        try {
          const tagList = JSON.parse(item.video_tags);
          ai_video_tags_en = tagList
            .map((item: any) => {
              const children = item.children
                .map((c: any) => c.label_en)
                .join(', ');
              return `${item.label_en} (${children})`;
            })
            .join(';\n\n');
          ai_video_tags_cn = tagList
            .map((item: any) => {
              const children = item.children
                .map((c: any) => c.label_zh)
                .join(', ');
              return `${item.label_zh} (${children})`;
            })
            .join(';\n\n');
        } catch (err) {
          console.error('Invalid JSON in video_tags:', err);
          ai_video_tags_en = '-';
          ai_video_tags_cn = '-';
        }
      }

      return {
        ...item,
        summary_en: item?.video_summary_en || summary_en,
        summary_cn: item?.video_summary_zh || summary_cn,
        summary_sentiment,
        d_summary_en,
        d_summary_cn,
        d_summary_sentiment,
        selling_points,
        video_categories,
        ai_video_tags_en,
        ai_video_tags_cn,
        ...obj,
      };
    });

    return { list: data, total };
  },
};

export const TagAnalysisDownload: DownloadConfigInfoType = {
  columns: ({ auxiliaryParams }) => {
    const { title, metric, attributeBy, videoTagsTier, tagsList } =
      auxiliaryParams;

    const valueText =
      METRIC_OPTIONS.find(option => option.value === metric)?.text || 'Value';

    // Base columns
    const baseColumns = [
      { key: 'title', header: 'Tier 1 Tags' },
      {
        key: 'label_en',
        header: videoTagsTier === 't1' ? 'Tier 1 Tags' : 'Tier 2 Tags',
      },
      { key: 'value', header: valueText },
    ];

    const attributeColumns = {
      language: {
        key: 'language',
        header: 'Language',
        renderHelper: [languageRenderHelper],
      },
      platform: {
        key: 'platform',
        header: 'Platform',
        renderHelper: [channelRenderHelper],
      },
      time: {
        key: 'time',
        header: 'Date',
      },
    };

    const attributeColumn =
      attributeColumns?.[attributeBy as keyof typeof attributeColumns] || null;

    if (videoTagsTier === 't1' && attributeBy !== 'total') {
      const tagsColumns =
        tagsList?.map((val: string) => ({
          key: val,
          header: val,
        })) || [];

      return [attributeColumn, ...tagsColumns];
    }

    let finalColumns = attributeColumn
      ? [attributeColumn, ...baseColumns]
      : baseColumns;

    // "Overview" removes the "title" column
    if (title === 'Overview') {
      finalColumns = finalColumns.filter(col => col.key !== 'title');
    }

    return finalColumns;
  },
};
